interface BrandLoaderProps {
    /**
     * Fill the viewport with a parchment backdrop. Use for app-boot / auth waits
     * that render *before* the site header mounts. Defaults to false, which fills
     * the parent (the content area beneath an already-mounted header).
     */
    readonly fullScreen?: boolean;
    /**
     * Show the "The Savory Grimoire" wordmark beneath the mark. Leave off inside
     * the app shell, where the header already shows it. Defaults to `fullScreen`.
     */
    readonly showWordmark?: boolean;
    /** Caption under the mark. Defaults to "Loading…". */
    readonly label?: string;
}

/**
 * Themed loading state: the Grimoire mark gently breathes over a soft accent
 * glow, with the Cinzel wordmark on full-screen boots. Replaces the bare
 * "Loading..." text across route fallbacks and auth gates.
 */
export const BrandLoader = ({ fullScreen = false, showWordmark = fullScreen, label = "Loading…" }: BrandLoaderProps) => {
    return (
        <div
            role="status"
            aria-live="polite"
            className={
                "flex flex-col items-center justify-center gap-5 px-4 text-center " +
                (fullScreen ? "min-h-screen bg-[var(--bg)]" : "w-full py-24")
            }
        >
            <div className="relative flex items-center justify-center">
                <span
                    aria-hidden="true"
                    className="grimoire-loader__glow absolute inset-0 m-auto h-36 w-36 rounded-full blur-xl"
                />
                <img
                    src="/savorygrimoire-512.png"
                    alt=""
                    aria-hidden="true"
                    className={"grimoire-loader__mark relative w-auto select-none " + (fullScreen ? "h-24 sm:h-28" : "h-16")}
                />
            </div>

            {showWordmark && (
                <span className="font-display text-lg uppercase tracking-[0.18em] text-[var(--fg-1)] sm:text-xl">
                    The Savory Grimoire
                </span>
            )}

            <span className="font-serif text-sm italic text-[var(--fg-muted)]">{label}</span>
        </div>
    );
};
